import { Loader, Modal, SegmentedControl } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CloudPackageCategory,
    InstanceResponseModel,
} from '../../../../../apis/v1/instances/model';
import { Button } from '../../../../../components/Atoms';
import InstanceFamilyOptionList from '../../../../../components/Organisms/InstanceFamilyOptionList/InstanceFamilyOptionList';
import { useResizeInstanceMutate } from '../../../../../hooks/CloudComputeHooks/useResizeInstanceMutate';
import { useResizeOptionsQuery } from '../hooks/useResizeOptionsQuery';

interface ResizeInstanceModalProps {
    opened: boolean;
    onClose: () => void;
    instance: InstanceResponseModel;
}

export default function ResizeInstanceModal({
    opened,
    onClose,
    instance,
}: ResizeInstanceModalProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const resizeMutation = useResizeInstanceMutate();
    const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<CloudPackageCategory | null>(null);

    const { data, isPending, isError } = useResizeOptionsQuery(instance.id, opened);

    const isWindows = instance.image.os.toLowerCase().includes('windows');
    const packages = useMemo(
        () =>
            (data?.packages ?? []).filter((pkg) =>
                isWindows ? pkg.cpu >= 1 && pkg.memory / 1024 > 1 : true
            ),
        [data, isWindows]
    );

    const categories = useMemo(() => [...new Set(packages.map((pkg) => pkg.category))], [packages]);

    const defaultCategory = useMemo(() => {
        if (categories.length === 0) return null;
        const current = instance.package?.category;
        return current && categories.includes(current) ? current : categories[0];
    }, [categories, instance.package?.category]);
    const effectiveCategory =
        selectedCategory && categories.includes(selectedCategory)
            ? selectedCategory
            : defaultCategory;

    useEffect(() => {
        if (!opened) {
            setSelectedSlug(null);
            setSelectedCategory(null);
        }
    }, [opened]);

    useEffect(() => {
        if (opened && instance.state.power === 'resizing' && !resizeMutation.isPending) {
            onClose();
        }
    }, [opened, instance.state.power, resizeMutation.isPending, onClose]);

    const visiblePackages = useMemo(
        () => packages.filter((pkg) => !effectiveCategory || pkg.category === effectiveCategory),
        [packages, effectiveCategory]
    );

    useEffect(() => {
        if (selectedSlug && !visiblePackages.some((pkg) => pkg.slug === selectedSlug)) {
            setSelectedSlug(null);
        }
    }, [visiblePackages, selectedSlug]);

    const options = useMemo(
        () =>
            visiblePackages.map((pkg) => ({
                sku: pkg.slug,
                vcpu: pkg.cpu,
                memory: `${pkg.memory / 1024} GB`,
                ssd: `${pkg.disk + instance.spec.diskAddition} GB`,
                bandwidth: `${pkg.bandwidth} TB`,
                priceHr: `${pkg.price.hourly.toFixed(2)}`,
                priceMo: `${pkg.price.monthly.toFixed(2)}`,
            })),
        [visiblePackages, instance.spec.diskAddition]
    );

    const selectedPackage = visiblePackages.find((pkg) => pkg.slug === selectedSlug);
    const isSaving = resizeMutation.isPending;
    const noOptions = !isPending && !isError && packages.length === 0;
    const confirmDisabled =
        !selectedPackage || isSaving || isPending || instance.state.power === 'resizing';

    const handleConfirm = () => {
        if (!selectedPackage || confirmDisabled) return;
        resizeMutation.mutate(
            { id: instance.id, data: { packageId: selectedPackage.id } },
            { onSuccess: () => onClose() }
        );
    };

    return (
        <Modal
            size="xl"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
            }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">{t('resize.modalTitle')}</span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">{t('resize.modalDescription')}</p>
                </div>

                {categories.length > 1 && (
                    <SegmentedControl
                        aria-label={t('resize.categoryLabel')}
                        value={effectiveCategory ?? ''}
                        onChange={(value) => setSelectedCategory(value as CloudPackageCategory)}
                        data={categories.map((category) => ({
                            value: category,
                            label: t(`resize.category.${category}`),
                        }))}
                        disabled={isSaving}
                    />
                )}

                {isError ? (
                    <p className="w-full text-sm text-fg/68 py-2">
                        {t('resize.optionsLoadFailed')}
                    </p>
                ) : noOptions ? (
                    <p className="w-full text-sm text-fg/68 py-2">{t('resize.noOptions')}</p>
                ) : (
                    <div className="w-full">
                        <InstanceFamilyOptionList
                            options={options}
                            selectedSku={selectedSlug ?? undefined}
                            onSelectInstance={setSelectedSlug}
                            isLoading={isPending}
                        />
                    </div>
                )}

                <p className="text-xs text-yellow-400/80 light:text-yellow-800">
                    {t('resize.warning')}
                </p>

                <div className="w-full flex flex-col justify-start items-start gap-2.5">
                    <Button width="100%" onClick={handleConfirm} disabled={confirmDisabled}>
                        {isSaving ? (
                            <div className="flex items-center gap-2">
                                <Loader size={16} color="white" />
                                <span>{t('resize.resizing')}</span>
                            </div>
                        ) : (
                            t('resize.confirmButton')
                        )}
                    </Button>
                    <Button width="100%" variant="secondary" onClick={onClose} disabled={isSaving}>
                        {t('resize.cancel')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
